import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/atlas/PageHeader';
import { PeriodSelector, type Period } from '../../components/atlas/PeriodSelector';
import { useAuth } from '../../contexts/AuthContext';
import { VendasTab } from './insights/VendasTab';
import { ReceitaTab } from './insights/ReceitaTab';
import { SaudeTab } from './insights/SaudeTab';
import { EquipeTab } from './insights/EquipeTab';

type TabId = 'vendas' | 'receita' | 'saude' | 'equipe';

const ALL_TABS: { id: TabId; label: string; admin?: boolean }[] = [
  { id: 'vendas', label: 'Vendas' },
  { id: 'receita', label: 'Receita', admin: true },
  { id: 'saude', label: 'Saúde' },
  { id: 'equipe', label: 'Equipe' },
];

export default function InsightsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [params, setParams] = useSearchParams();

  const visibleTabs = ALL_TABS.filter((t) => !t.admin || isAdmin);
  const requestedTab = (params.get('tab') as TabId) || 'vendas';
  const tab: TabId = visibleTabs.some((t) => t.id === requestedTab) ? requestedTab : 'vendas';
  const period = (params.get('period') as Period) || '6m';

  function setTab(id: TabId) {
    const next = new URLSearchParams(params);
    next.set('tab', id);
    setParams(next, { replace: true });
  }

  function setPeriod(p: Period) {
    const next = new URLSearchParams(params);
    next.set('period', p);
    setParams(next, { replace: true });
  }

  const showPeriodSelector = tab === 'vendas' || tab === 'receita';

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Insights"
        title="Pulsar do CRM"
        subtitle="Vendas, receita, saúde do pipeline e desempenho da equipe."
        actions={
          showPeriodSelector ? <PeriodSelector value={period} onChange={setPeriod} /> : undefined
        }
      />

      <nav className="flex gap-1 border-b border-border sticky top-0 bg-canvas/80 backdrop-blur z-10">
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`text-sm px-4 py-2 -mb-px border-b-2 transition-colors ${
              tab === t.id
                ? 'border-ink-primary text-ink-primary font-medium'
                : 'border-transparent text-ink-tertiary hover:text-ink-secondary'
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'vendas' && <VendasTab period={period} />}
      {tab === 'receita' && isAdmin && <ReceitaTab period={period} />}
      {tab === 'saude' && <SaudeTab />}
      {tab === 'equipe' && <EquipeTab />}
    </div>
  );
}
